import { 
  appendRowToSheet, 
  batchAppendRowsToSheet,
  ensureTrackingTabsExist, 
  getSheetsAccessToken 
} from '@/lib/googleSheets';

export interface TrackingEvent {
  id?: string;
  type: 'form' | 'click';
  eventName: string;
  label: string;
  targetUrl?: string;
  pageUrl: string;
  email?: string;
  ctaPosition?: string;
  details?: Record<string, any> | string;
  createdAt: string;
  synced: boolean;
  syncedAt?: string;
}

export interface SheetsTrackingConfig {
  spreadsheetId: string;
  spreadsheetUrl: string;
  spreadsheetTitle: string;
  connectedEmail?: string;
  autoSyncEnabled?: boolean;
  lastSyncedAt?: string;
}

const SETTINGS_DOC_ID = 'google_sheets_tracking';

/**
 * Loads the saved Google Sheets tracking configuration from Firestore.
 */
export const getSheetsTrackingConfig = async (): Promise<SheetsTrackingConfig | null> => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
    return null;
  }
  try {
    const { db } = await import('@/lib/firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const docRef = doc(db, 'site_settings', SETTINGS_DOC_ID);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as SheetsTrackingConfig;
    }
    return null;
  } catch (err: any) {
    return null;
  }
};

/**
 * Saves the Google Sheets tracking configuration in Firestore.
 */
export const saveSheetsTrackingConfig = async (config: Partial<SheetsTrackingConfig>): Promise<void> => {
  const { db } = await import('@/lib/firebase');
  const { doc, setDoc } = await import('firebase/firestore');
  const docRef = doc(db, 'site_settings', SETTINGS_DOC_ID);
  await setDoc(docRef, {
    ...config,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

/**
 * Checks whether user has explicitly granted cookie/tracking consent.
 */
export const hasTrackingConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('fivelens_cookie_consent') === 'granted';
};

/**
 * Records a tracking event in Firestore and immediately appends to Google Sheet if token is available.
 */
export const recordTrackingEvent = async (event: {
  type: 'form' | 'click';
  eventName: string;
  label: string;
  targetUrl?: string;
  pageUrl?: string;
  email?: string;
  ctaPosition?: string;
  details?: Record<string, any> | string;
}): Promise<string | null> => {
  try {
    // Gate first-party tracking events: Behavioural logging tied to visitors requires consent
    if (typeof window !== 'undefined') {
      const isAdminPath = window.location.pathname.startsWith('/admin');
      if (!isAdminPath && !hasTrackingConsent()) {
        return null;
      }
    }

    const pageUrl = event.pageUrl || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const nowIso = new Date().toISOString();
    let isSynced = false;

    // Attempt direct sync to Google Sheet if token & config exist
    const token = getSheetsAccessToken();
    const config = await getSheetsTrackingConfig();

    if (token && config?.spreadsheetId) {
      try {
        await ensureTrackingTabsExist(config.spreadsheetId, token);
        if (event.type === 'form') {
          await appendRowToSheet(
            config.spreadsheetId,
            'Form Submissions',
            [
              nowIso,
              event.eventName,
              pageUrl,
              event.email || '',
              typeof event.details === 'object' ? JSON.stringify(event.details) : event.details || '',
              event.ctaPosition || '',
              typeof navigator !== 'undefined' ? navigator.userAgent : ''
            ],
            token
          );
          isSynced = true;
        } else {
          await appendRowToSheet(
            config.spreadsheetId,
            'Link & Button Clicks',
            [
              nowIso,
              event.eventName,
              event.label,
              event.targetUrl || '',
              pageUrl,
              event.ctaPosition || '',
              typeof event.details === 'object' ? JSON.stringify(event.details) : event.details || ''
            ],
            token
          );
          isSynced = true;
        }
      } catch (sheetErr) {
        console.warn('Real-time Google Sheet write queued (will sync later):', sheetErr);
        isSynced = false;
      }
    }

    // Persist to Firestore tracking_events collection
    const { db } = await import('@/lib/firebase');
    const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
    const docRef = await addDoc(collection(db, 'tracking_events'), {
      type: event.type,
      eventName: event.eventName,
      label: event.label,
      targetUrl: event.targetUrl || '',
      pageUrl,
      email: event.email || '',
      ctaPosition: event.ctaPosition || '',
      details: typeof event.details === 'object' ? JSON.stringify(event.details) : event.details || '',
      createdAt: nowIso,
      synced: isSynced,
      syncedAt: isSynced ? nowIso : null,
      serverTime: serverTimestamp()
    });

    return docRef.id;
  } catch (error: any) {
    const msg = String(error?.message || error?.code || error || '').toLowerCase();
    if (
      msg.includes('quota') ||
      msg.includes('resource-exhausted') ||
      msg.includes('resource_exhausted') ||
      msg.includes('limit') ||
      msg.includes('offline') ||
      msg.includes('unavailable')
    ) {
      console.warn('Firestore tracking event write skipped due to quota or offline state.');
    } else {
      console.error('Error recording tracking event:', error);
    }
    return null;
  }
};

/**
 * Fetches recent tracking events from Firestore.
 */
export const getRecentTrackingEvents = async (limitCount = 100): Promise<TrackingEvent[]> => {
  try {
    const { db } = await import('@/lib/firebase');
    const { collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
    const q = query(
      collection(db, 'tracking_events'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as TrackingEvent[];
  } catch (error) {
    console.warn('Error fetching tracking events:', error);
    return [];
  }
};

/**
 * Syncs all un-synced events from Firestore to the connected Google Spreadsheet.
 */
export const syncPendingEventsToSheet = async (
  spreadsheetId: string,
  token?: string
): Promise<{ syncedForms: number; syncedClicks: number }> => {
  const accessToken = token || getSheetsAccessToken();
  if (!accessToken) {
    throw new Error('Google Sheets access token missing. Please sign in with Google first.');
  }

  // Ensure tabs exist before trying to append
  await ensureTrackingTabsExist(spreadsheetId, accessToken);

  const { db } = await import('@/lib/firebase');
  const { collection, query, where, limit, getDocs, updateDoc, doc } = await import('firebase/firestore');

  // Fetch un-synced events
  const q = query(
    collection(db, 'tracking_events'),
    where('synced', '==', false),
    limit(250)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) {
    return { syncedForms: 0, syncedClicks: 0 };
  }

  const formEvents: { docId: string; row: string[] }[] = [];
  const clickEvents: { docId: string; row: string[] }[] = [];

  snapshot.forEach(docSnap => {
    const data = docSnap.data();
    if (data.type === 'form') {
      formEvents.push({
        docId: docSnap.id,
        row: [
          data.createdAt || new Date().toISOString(),
          data.eventName || 'form_submit',
          data.pageUrl || '',
          data.email || '',
          typeof data.details === 'object' ? JSON.stringify(data.details) : data.details || '',
          data.ctaPosition || '',
          'Synced from queue'
        ]
      });
    } else {
      clickEvents.push({
        docId: docSnap.id,
        row: [
          data.createdAt || new Date().toISOString(),
          data.eventName || 'click',
          data.label || '',
          data.targetUrl || '',
          data.pageUrl || '',
          data.ctaPosition || '',
          typeof data.details === 'object' ? JSON.stringify(data.details) : data.details || ''
        ]
      });
    }
  });

  // Batch append form submissions
  if (formEvents.length > 0) {
    await batchAppendRowsToSheet(
      spreadsheetId,
      'Form Submissions',
      formEvents.map(f => f.row),
      accessToken
    );
  }

  // Batch append clicks
  if (clickEvents.length > 0) {
    await batchAppendRowsToSheet(
      spreadsheetId,
      'Link & Button Clicks',
      clickEvents.map(c => c.row),
      accessToken
    );
  }

  // Mark all synced in Firestore
  const nowIso = new Date().toISOString();
  const allDocIds = [...formEvents.map(f => f.docId), ...clickEvents.map(c => c.docId)];
  for (const docId of allDocIds) {
    try {
      await updateDoc(doc(db, 'tracking_events', docId), {
        synced: true,
        syncedAt: nowIso
      });
    } catch (e) {
      console.warn('Could not update sync flag for doc:', docId);
    }
  }

  // Update lastSyncedAt in config
  await saveSheetsTrackingConfig({
    lastSyncedAt: nowIso
  });

  return {
    syncedForms: formEvents.length,
    syncedClicks: clickEvents.length
  };
};

let waListenerAdded = false;
export function initWhatsAppGa4Tracking() {
  if (waListenerAdded || typeof window === 'undefined') return;
  waListenerAdded = true;
  document.addEventListener('click', (event) => {
    const link = (event.target as Element)?.closest?.('a');
    const href = link?.getAttribute('href') || '';
    if (!(href.includes('wa.me') || href.includes('whatsapp'))) return;
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', 'whatsapp_click', { link_url: href, link_text: (link as HTMLElement).innerText?.trim() || link?.getAttribute('aria-label') || href, page_location: location.href, transport_type: 'beacon' });
  }, true);
}

let isGlobalListenerInitialized = false;

/**
 * Initializes automatic global tracking for all buttons, links, and forms.
 */
export const initGlobalTrackingListener = () => {
  if (isGlobalListenerInitialized || typeof window === 'undefined') return;
  isGlobalListenerInitialized = true;

  // Intercept click events
  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      if (!hasTrackingConsent()) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Find closest clickable element (anchor, button, or input submit)
      const anchor = target.closest('a') as HTMLAnchorElement | null;
      const button = target.closest('button') as HTMLButtonElement | null;
      const submitInput = target.closest('input[type="submit"]') as HTMLInputElement | null;

      if (anchor) {
        const href = anchor.getAttribute('href') || '';
        const text = anchor.innerText?.trim() || anchor.getAttribute('aria-label') || anchor.title || href;
        
        // Skip purely in-page hashes or empty anchors
        if (!href || href === '#' || href.startsWith('javascript:')) return;

        let eventName = 'link_click';
        if (href.startsWith('mailto:')) eventName = 'mailto_click';
        else if (href.startsWith('tel:')) eventName = 'phone_click';
        else if (href.includes('wa.me') || href.includes('whatsapp')) eventName = 'whatsapp_click';
        else if (href.includes('booking') || text.toLowerCase().includes('discovery call')) {
          eventName = 'discovery_call_click';
        } else if (text.toLowerCase().includes('register')) {
          eventName = 'registration_click';
        }

        recordTrackingEvent({
          type: 'click',
          eventName,
          label: text.substring(0, 100),
          targetUrl: href,
          pageUrl: window.location.pathname,
          ctaPosition: anchor.getAttribute('data-cta') || anchor.id || undefined
        });
      } else if (button || submitInput) {
        const btn = button || submitInput;
        const text = btn?.innerText?.trim() || (btn as any)?.value || btn?.getAttribute('aria-label') || 'Button';
        const type = btn?.getAttribute('type') || 'button';
        
        // Skip internal admin toggle controls to keep sheet clean, but track all user-facing buttons
        if (window.location.pathname.startsWith('/admin') && (text.includes('Filter') || text.includes('Toggle'))) {
          return;
        }

        let eventName = 'button_click';
        if (text.toLowerCase().includes('discovery call') || text.toLowerCase().includes('book')) {
          eventName = 'discovery_call_click';
        } else if (text.toLowerCase().includes('register')) {
          eventName = 'registration_click';
        } else if (text.toLowerCase().includes('subscribe') || text.toLowerCase().includes('newsletter')) {
          eventName = 'newsletter_click';
        }

        recordTrackingEvent({
          type: 'click',
          eventName,
          label: text.substring(0, 100),
          pageUrl: window.location.pathname,
          ctaPosition: btn?.getAttribute('data-cta') || btn?.id || undefined,
          details: { buttonType: type }
        });
      }
    },
    { capture: true, passive: true }
  );

  // Intercept form submissions
  document.addEventListener(
    'submit',
    (event: SubmitEvent) => {
      if (!hasTrackingConsent()) return;

      const form = event.target as HTMLFormElement | null;
      if (!form) return;

      // Skip admin forms (like password login or link editing forms)
      if (window.location.pathname.startsWith('/admin')) return;

      const formId = form.id || form.getAttribute('name') || form.getAttribute('data-form-name') || 'website_form';
      
      // Extract email if entered
      const emailInput = form.querySelector('input[type="email"], input[name="email"]') as HTMLInputElement | null;
      const email = emailInput?.value || '';

      // Extract form title or submit button text
      const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]') as HTMLElement | null;
      const label = submitBtn?.innerText?.trim() || formId;

      recordTrackingEvent({
        type: 'form',
        eventName: formId.includes('newsletter') ? 'newsletter_signup' : 'form_submit',
        label,
        pageUrl: window.location.pathname,
        email: email || undefined,
        ctaPosition: form.getAttribute('data-cta') || undefined
      });
    },
    { capture: true, passive: true }
  );
};
